/** Create-session entry point — Google-Meet-style rebuild.
 *
 * Two clear modes up front: **Start now** (ad-hoc, jump straight in) vs
 * **Schedule for later** (24h date+time picker). An optional title names the
 * session. The operator can free-add participants right from the modal; the
 * backend issues an Invite per person (emailed/SMSed when it carries that
 * channel). This is a DIRECT session — campaign sessions are created from the
 * campaign flow, so there's no campaign picker here (Sessions v2).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { BeneficiaryFormFields } from "@/components/BeneficiaryFormFields";
import {
  beneficiaryFormToSessionParticipant,
  emptyBeneficiaryForm,
  type BeneficiaryForm,
} from "@/components/beneficiaryForm";
import { MuiDateTimePicker } from "@/components/MuiDateTimePicker";
import { SlotAvailability } from "@/components/SlotAvailability";
import { ApiPaths, api } from "@/lib/api";
import type { Session } from "@/lib/types";
import { useSlotGrid } from "@/lib/slotGrid";


// Wire shape sent to the API — `country` maps to the invite's
// `address_country` column.
type ParticipantWire = ReturnType<typeof beneficiaryFormToSessionParticipant>;

type CreatePayload = {
  scheduled_for?: string;
  title?: string;
  notes?: string;
  participants?: ParticipantWire[];
};


export function CreateSessionButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn-primary"
        onClick={() => setOpen(true)}
      >
        {t("ui.createSession.createBtn")}
      </button>
      {open && <CreateSessionModal onClose={() => setOpen(false)} />}
    </>
  );
}


function quotaErrorMessage(err: unknown, t: (key: string) => string): string {
  const r = (err as { response?: { status?: number; data?: { reason?: string; detail?: string } } })
    .response;
  if (r?.status === 402) {
    if (r.data?.reason === "subscription_past_due") {
      return t("ui.createSession.errorPastDue");
    }
    if (r.data?.reason === "free_plan_minutes_exhausted") {
      return t("ui.createSession.errorMinutesExhausted");
    }
    return r.data?.detail ?? t("ui.createSession.errorQuotaBlocked");
  }
  return (err as Error).message ?? t("ui.createSession.errorGeneric");
}


function CreateSessionModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"now" | "schedule">("now");
  const [scheduledFor, setScheduledFor] = useState(""); // ISO
  const grid = useSlotGrid();
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [participants, setParticipants] = useState<BeneficiaryForm[]>([]);

  const create = useMutation({
    mutationFn: async (payload: CreatePayload) => {
      const res = await api.post<Session>(ApiPaths.sessions, payload);
      return res.data;
    },
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      navigate(`/sessions/${s.id}`);
    },
  });

  const submit = () => {
    const payload: CreatePayload = {};
    if (mode === "schedule" && scheduledFor) {
      payload.scheduled_for = scheduledFor; // already a full ISO instant
    }
    if (title.trim()) payload.title = title.trim();
    if (notes.trim()) payload.notes = notes.trim();
    const people: ParticipantWire[] = participants
      .filter((p) => p.first_name.trim())
      .map(beneficiaryFormToSessionParticipant);
    if (people.length) payload.participants = people;
    create.mutate(payload);
  };

  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const scheduleInvalid = mode === "schedule" && !scheduledFor;

  const setP = (i: number, patch: Partial<BeneficiaryForm>) =>
    setParticipants((arr) =>
      arr.map((x, j) => (j === i ? { ...x, ...patch } : x)),
    );

  return (
    <div
      className="modal-backdrop bg-black/60"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-h-[88vh] w-full max-w-lg space-y-4 overflow-auto rounded-lg border border-nb-border bg-nb-bg p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-nb-text">
            {t("ui.createSession.title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-nb-text-3 hover:text-nb-text"
            aria-label={t("ui.createSession.close")}
          >
            ×
          </button>
        </header>

        {/* Mode: start now vs schedule (Google-Meet-style) */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMode("now")}
            className={`rounded-md border p-3 text-left text-sm ${
              mode === "now"
                ? "border-nb-mint bg-nb-surface-2 text-nb-text"
                : "border-nb-border text-nb-text-2 hover:bg-nb-surface"
            }`}
          >
            <div className="font-medium">▶ {t("ui.createSession.startNow")}</div>
            <div className="text-xs text-nb-text-3">
              {t("ui.createSession.startNowDesc")}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode("schedule")}
            className={`rounded-md border p-3 text-left text-sm ${
              mode === "schedule"
                ? "border-nb-mint bg-nb-surface-2 text-nb-text"
                : "border-nb-border text-nb-text-2 hover:bg-nb-surface"
            }`}
          >
            <div className="font-medium">🗓 {t("ui.createSession.scheduleLater")}</div>
            <div className="text-xs text-nb-text-3">{t("ui.createSession.scheduleLaterDesc")}</div>
          </button>
        </div>

        {mode === "schedule" && (
          <div className="space-y-2">
            <label className="block space-y-1 text-sm">
              <span className="text-nb-text-2">{t("ui.createSession.dateTimeLabel")}</span>
              <MuiDateTimePicker
                value={scheduledFor}
                onChange={setScheduledFor}
                minDate={minDate}
                {...grid}
              />
            </label>
            {/* What's already booked that day — picking blind is how you
                double-book. */}
            <SlotAvailability value={scheduledFor} onPick={setScheduledFor} />
          </div>
        )}

        <label className="block space-y-1 text-sm">
          <span className="text-nb-text-2">{t("ui.createSession.titleLabel")}</span>
          <input
            className="input w-full"
            placeholder={t("ui.createSession.titlePlaceholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        {/* Free-add participants */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-nb-text-2">{t("ui.createSession.addParticipants")}</span>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() =>
                setParticipants((p) => [...p, { ...emptyBeneficiaryForm }])
              }
            >
              {t("ui.createSession.addPerson")}
            </button>
          </div>
          {participants.map((p, i) => (
            <div
              key={i}
              className="space-y-2 rounded-md border border-nb-border bg-nb-surface/30 p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-nb-text-3">
                  {t("ui.createSession.participantN", { n: i + 1 })}
                </span>
                <button
                  type="button"
                  className="text-xs text-nb-text-3 hover:text-cvp-danger"
                  aria-label={t("ui.createSession.removeParticipantAria", { n: i + 1 })}
                  onClick={() =>
                    setParticipants((arr) => arr.filter((_, j) => j !== i))
                  }
                >
                  {t("ui.createSession.remove")}
                </button>
              </div>
              {/* Same identity + legal-entity + address capture as a
                  beneficiary — the backend upserts a session_participant
                  Contact from it (Phase 3). Custom fields don't apply here. */}
              <BeneficiaryFormFields
                form={p}
                onChange={(patch) => setP(i, patch)}
                customFields={{}}
                onCustomFieldChange={() => {}}
                hideCustomFields
              />
            </div>
          ))}
          {participants.length === 0 && (
            <p className="text-xs text-nb-text-3">
              {t("ui.createSession.participantsHint")}
            </p>
          )}
        </div>

        <label className="block space-y-1 text-sm">
          <span className="text-nb-text-2">
            {t("ui.createSession.notesLabel")}
          </span>
          <textarea
            className="input w-full"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("ui.createSession.notesPlaceholder")}
            aria-label={t("ui.createSession.notesAria")}
          />
        </label>

        {create.isError && (
          <p className="text-xs text-cvp-danger">
            {quotaErrorMessage(create.error, t)}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {t("ui.createSession.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={submit}
            disabled={create.isPending || scheduleInvalid}
          >
            {create.isPending
              ? t("ui.createSession.creating")
              : mode === "now"
                ? t("ui.createSession.startNow")
                : t("ui.createSession.scheduleBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
